import { isFuture, isToday, parseISO } from 'date-fns';
import type { OptionsWithTZ } from 'date-fns-tz';
import { format, utcToZonedTime } from 'date-fns-tz';
import { isString } from 'lodash-es';

import {
  COLOR_DATETIME_FORMAT,
  COLOR_DATETIME_WITH_TIMEZONE_FORMAT,
  COLOR_DATE_FORMAT,
  COLOR_LONG_DATETIME_WITH_TIMEZONE_FORMAT,
  COLOR_LONG_DATE_FORMAT,
  COLOR_TIMEZONE_FORMAT,
  COLOR_TIME_FORMAT,
  COLOR_TIME_WITH_TIMEZONE_FORMAT,
} from './constants';
import type { IDefinedDateRange, IIntlDateTimeFormatOptions } from './types';

const VALID_DATETIME_FORMATS = [
  COLOR_DATE_FORMAT,
  COLOR_LONG_DATE_FORMAT,
  COLOR_TIME_FORMAT,
  COLOR_DATETIME_FORMAT,
  COLOR_TIMEZONE_FORMAT,
  COLOR_DATETIME_WITH_TIMEZONE_FORMAT,
  COLOR_LONG_DATETIME_WITH_TIMEZONE_FORMAT,
  COLOR_TIME_WITH_TIMEZONE_FORMAT,
];

class DateTimeFormatError extends Error {
  constructor(dateFormat: string) {
    super(`Unexpected date time format ${dateFormat}`);
  }
}

function getZonedDateTimeFromDateTime(
  dateTime: Date | string,
  timeZone: OptionsWithTZ['timeZone']
): Date {
  let dateObj = isString(dateTime) ? parseISO(dateTime) : dateTime;
  if (timeZone) {
    dateObj = utcToZonedTime(dateTime, timeZone);
  }
  return dateObj;
}

/**
 * Formats a date according to the given format.
 * @param dateTime - The date to format. If string, will parse it to a date object
 */
export function formatDateTimeWithFormat(
  dateTime: Date | string,
  dateFormat: string,
  options?: OptionsWithTZ
): string {
  if (!VALID_DATETIME_FORMATS.includes(dateFormat)) {
    throw new DateTimeFormatError(dateFormat);
  }
  return format(getZonedDateTimeFromDateTime(dateTime, options?.timeZone), dateFormat, options);
}

/**
 * Return a formatted date string e.g. "Apr 20, 2020"
 */
export function formatDate(date: Date | string, options?: OptionsWithTZ): string {
  return formatDateTimeWithFormat(date, COLOR_DATE_FORMAT, options);
}

export function formatLongDate(date: Date | string, options?: OptionsWithTZ): string {
  return formatDateTimeWithFormat(date, COLOR_LONG_DATE_FORMAT, options);
}

export function formatDateTime(date: Date | string, options?: OptionsWithTZ): string {
  return formatDateTimeWithFormat(date, COLOR_DATETIME_FORMAT, options);
}

export function formatTime(date: Date | string, options?: OptionsWithTZ): string {
  return formatDateTimeWithFormat(date, COLOR_TIME_FORMAT, options);
}

export function formatTimeZone(date: Date | string, options?: OptionsWithTZ): string {
  return formatDateTimeWithFormat(date, COLOR_TIMEZONE_FORMAT, options);
}

export function formatTimeWithTimeZone(date: Date | string, options?: OptionsWithTZ): string {
  return formatDateTimeWithFormat(date, COLOR_TIME_WITH_TIMEZONE_FORMAT, options);
}

export function formatDateTimeWithTimeZone(date: Date | string, options?: OptionsWithTZ): string {
  return formatDateTimeWithFormat(date, COLOR_DATETIME_WITH_TIMEZONE_FORMAT, options);
}

export function formatLongDateTimeWithTimeZone(
  date: Date | string,
  options?: OptionsWithTZ
): string {
  return formatDateTimeWithFormat(date, COLOR_LONG_DATETIME_WITH_TIMEZONE_FORMAT, options);
}

/**
 * Return a formatted short date string e.g. "Apr 20"
 */
export function formatShortDate(date: Date | string, options?: IIntlDateTimeFormatOptions): string {
  const zonedTime = getZonedDateTimeFromDateTime(date, options?.timeZone);
  return zonedTime.toLocaleDateString(options?.locale, { month: 'short', day: 'numeric' });
}

/**
 * Return a formatted date string without year e.g. "April 20"
 */
export function formatDateWithoutYear(
  date: Date | string,
  options?: IIntlDateTimeFormatOptions
): string {
  const zonedTime = getZonedDateTimeFromDateTime(date, options?.timeZone);
  return zonedTime.toLocaleDateString(options?.locale, { month: 'long', day: 'numeric' });
}

/**
 * Return a formatted date string with year e.g. "April 20, 2020"
 */
export function formatDateWithMonthAndYear(
  date: Date | string,
  options?: IIntlDateTimeFormatOptions
): string {
  const zonedTime = getZonedDateTimeFromDateTime(date, options?.timeZone);
  return zonedTime.toLocaleDateString(options?.locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatDateRange(dateRange: IDefinedDateRange): string {
  return `${formatShortDate(dateRange.start)} - ${formatShortDate(dateRange.end)}`;
}

/**
 * Return a formatted, localized, short time string or date string:
 * - If date is today, return time string e.g. "3:32 PM"
 * - If date is before today, return date string with 2 digit year, e.g. "4/17/23"
 * - If date is after today, return date string with 4 digit year, e.g. "4/17/2042"
 */
export function formatShortDateTime(dateTime: Date, options?: IIntlDateTimeFormatOptions): string {
  const zonedDateTime = getZonedDateTimeFromDateTime(dateTime, options?.timeZone);

  if (isToday(zonedDateTime)) {
    return zonedDateTime.toLocaleTimeString(options?.locale, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  return dateTime.toLocaleDateString(options?.locale, {
    month: 'numeric',
    day: 'numeric',
    year: isFuture(zonedDateTime) ? 'numeric' : '2-digit',
  });
}
